import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, FileText, ImagePlus, Link2, ListChecks, Loader2, Plus, SendHorizontal, UploadCloud, X } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { describeUploadFailure, MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, uploadBlob } from '@/lib/upload'
import { AttachmentsField, type AttachmentRef } from '@/components/composites/attachments-field'
import { filesFromClipboard } from '@/lib/clipboardFiles'
import { cardsApi } from '@/features/cards/api'
import type { Card } from '@/types/entities'
import { stopWorkIfOn } from '@/features/worktime'
import { reviewPackApi, reviewPackKey } from './api'
import { cleanBullets, isHttpLink } from './rules'

const DOC_ACCEPT = 'application/pdf,text/plain,text/csv,application/zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
const PHOTO_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/heic'

interface Picked {
  name: string
  state: 'uploading' | 'done' | 'failed'
  key?: string
  error?: string
  preview?: string
}

/** One required file: empty → uploading → done. Shown as a drop target so it reads as a slot to fill. */
function Slot({
  step,
  title,
  hint,
  accept,
  icon,
  isImage,
  value,
  onChange,
}: {
  step: number
  title: string
  hint: string
  accept: string
  icon: React.ReactNode
  isImage?: boolean
  value: Picked | null
  onChange: (next: Picked | null) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [hovered, setHovered] = useState(false)
  const takeRef = useRef<(file: File | undefined) => void>(() => {})

  // ⌘V / Ctrl+V while the pointer is over this slot fills it, the same as dropping a file.
  useEffect(() => {
    if (!hovered || value) return
    const onPaste = (event: ClipboardEvent) => {
      const files = filesFromClipboard(event.clipboardData).filter((f) => !isImage || f.type.startsWith('image/'))
      if (files.length === 0) return
      event.preventDefault()
      takeRef.current(files[0])
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [hovered, value, isImage])

  async function take(file: File | undefined) {
    if (!file) return
    if (file.size > MAX_UPLOAD_BYTES) {
      onChange({ name: file.name, state: 'failed', error: `Larger than the ${MAX_UPLOAD_MB}MB limit.` })
      return
    }
    if (!file.type) {
      onChange({ name: file.name, state: 'failed', error: 'That kind of file cannot be attached.' })
      return
    }
    if (isImage && !file.type.startsWith('image/')) {
      onChange({ name: file.name, state: 'failed', error: 'Choose a picture (PNG, JPG, WEBP…).' })
      return
    }
    const preview = isImage ? URL.createObjectURL(file) : undefined
    onChange({ name: file.name, state: 'uploading', preview })
    try {
      const key = await uploadBlob(file, file.name, file.type)
      onChange({ name: file.name, state: 'done', key, preview })
    } catch (err) {
      onChange({ name: file.name, state: 'failed', error: describeUploadFailure(err), preview })
    }
  }

  useEffect(() => {
    takeRef.current = (file) => void take(file)
  })
  const done = value?.state === 'done'
  return (
    <section
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'rounded-lg border bg-card p-3 transition-colors dur-fast',
        done ? 'border-success/40 bg-success-soft/30' : value?.state === 'failed' ? 'border-danger/50' : 'border-border',
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className={cn('flex size-5 items-center justify-center rounded-full text-micro font-semibold', done ? 'bg-success text-white' : 'bg-brand-soft text-brand')}>
          {done ? <CheckCircle2 className="size-3.5" /> : step}
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-micro uppercase tracking-wide text-danger">Required</span>
      </div>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          void take(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {value ? (
        <div className="flex items-center gap-3">
          {value.preview ? (
            <img src={value.preview} alt="" className="size-14 shrink-0 rounded-md border border-border object-cover" />
          ) : (
            <span className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-text-muted">{icon}</span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" title={value.name}>{value.name}</p>
            <p className={cn('text-tiny', value.state === 'failed' ? 'text-danger' : 'text-text-muted')}>
              {value.state === 'uploading' ? 'Uploading…' : value.state === 'failed' ? value.error : 'Ready'}
            </p>
          </div>
          {value.state === 'uploading' ? (
            <Loader2 className="size-4 animate-spin text-text-muted" />
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} aria-label={`Remove ${title}`}>
              <X className="size-4" />
            </Button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            void take(e.dataTransfer.files?.[0])
          }}
          className="flex w-full flex-col items-center gap-1 rounded-md border border-dashed border-border-strong px-3 py-4 text-center transition-colors dur-fast hover:border-brand/60 hover:bg-brand-soft/40"
        >
          <UploadCloud className="size-5 text-text-muted" />
          <span className="text-sm font-medium">Choose a file, drop it here{isImage ? ' or paste it (⌘V)' : ''}</span>
          <span className="text-tiny text-text-muted">{hint}</span>
        </button>
      )}
    </section>
  )
}

/**
 * Sending work for approval asks for four things, all of them, every time — a document, a
 * photo, a link and a short list of what was done. The approver is shown exactly this first.
 * The status change happens here, after the pack is saved, so there is no state where an item
 * waits for approval with nothing for the approver to look at.
 */
export function SubmitReviewDialog({
  card,
  open,
  onOpenChange,
  onSent,
}: {
  card: Card
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent?: (updated: Card) => void
}) {
  const queryClient = useQueryClient()
  const [doc, setDoc] = useState<Picked | null>(null)
  const [docMode, setDocMode] = useState<'writeup' | 'link' | 'file'>('writeup')
  const [docLink, setDocLink] = useState('')
  const [writeUp, setWriteUp] = useState('')
  const [photo, setPhoto] = useState<Picked | null>(null)
  const [link, setLink] = useState('')
  const [bullets, setBullets] = useState<string[]>([''])
  const [tried, setTried] = useState(false)
  const existingExtras = card.progressAttachments ?? []
  const [extraPhotos, setExtraPhotos] = useState<AttachmentRef[]>(() => existingExtras.map((a) => ({ key: a.key, name: a.name })))
  const [extrasBusy, setExtrasBusy] = useState(false)

  const points = cleanBullets(bullets)
  const linkOk = isHttpLink(link)
  const docOk =
    docMode === 'file' ? doc?.state === 'done' : docMode === 'link' ? isHttpLink(docLink) : writeUp.trim().length >= 20
  const photoOk = photo?.state === 'done'
  const ready = docOk && photoOk && linkOk && points.length > 0
  const missing = [!docOk && 'the documentation', !photoOk && 'a photo', !linkOk && 'a link', points.length === 0 && 'what was done'].filter(Boolean)

  const send = useMutation({
    mutationFn: async () => {
      // More photos than the one the pack requires go on the progress update, where the approver
      // already looks. Saved first, so a failure here leaves nothing half-sent.
      const same = extraPhotos.length === existingExtras.length && extraPhotos.every((r) => existingExtras.some((a) => a.key === r.key))
      if (!same) await cardsApi.patch(card.id, { progressAttachments: extraPhotos })
      await reviewPackApi.create(card.id, {
        ...(docMode === 'file' ? { document: { key: doc!.key!, name: doc!.name } } : docMode === 'link' ? { documentLink: docLink.trim() } : { writeUp: writeUp.trim() }),
        photo: { key: photo!.key!, name: photo!.name },
        link: link.trim(),
        bullets: points,
      })
      return card.status === 'in_review' ? card : cardsApi.patch(card.id, { status: 'in_review' })
    },
    onSuccess: (updated) => {
      toast.success('Sent for approval', { description: card.title })
      void stopWorkIfOn(queryClient, card.id)
      queryClient.setQueryData(qk.card(card.id), updated)
      void queryClient.invalidateQueries({ queryKey: reviewPackKey(card.id) })
      // The board keeps one cache entry per filter variant under ['board', id, 'cards', …]; the old
      // ['cards'] key never matched it, so the card sat in its old column until a reload.
      queryClient.setQueriesData<{ items: Card[] }>({ queryKey: qk.cardsPrefix(card.boardId) }, (page) =>
        page ? { ...page, items: page.items.map((c) => (c.id === updated.id ? updated : c)) } : page,
      )
      void queryClient.invalidateQueries({ queryKey: qk.cardsPrefix(card.boardId) })
      void queryClient.invalidateQueries({ queryKey: qk.board(card.boardId) })
      void queryClient.invalidateQueries({ queryKey: ['cards'] })
      onSent?.(updated)
      onOpenChange(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !send.isPending && onOpenChange(next)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SendHorizontal className="size-4 text-brand" /> Share for approval
          </DialogTitle>
          <DialogDescription>
            “{card.title}” — your approvers see this first, before they approve. Everything below is needed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <section className={cn('rounded-lg border bg-card p-3', docOk ? 'border-success/40 bg-success-soft/30' : tried ? 'border-danger/50' : 'border-border')}>
            <div className="mb-2 flex items-center gap-2">
              <span className={cn('flex size-5 items-center justify-center rounded-full text-micro font-semibold', docOk ? 'bg-success text-white' : 'bg-brand-soft text-brand')}>
                {docOk ? <CheckCircle2 className="size-3.5" /> : 1}
              </span>
              <h3 className="text-sm font-semibold">Documentation</h3>
              <span className="ml-auto text-micro uppercase tracking-wide text-danger">Required</span>
            </div>
            <div className="mb-2 inline-flex rounded-md border border-border p-0.5 text-xs" role="tablist" aria-label="How to document it">
              {([['writeup', 'Write it here'], ['link', 'A link'], ['file', 'Upload a file']] as const).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={docMode === id} onClick={() => setDocMode(id)} className={cn('rounded px-2.5 py-1', docMode === id ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                  {label}
                </button>
              ))}
            </div>
            {docMode === 'writeup' ? (
              <div className="grid gap-1">
                <Textarea value={writeUp} onChange={(e) => setWriteUp(e.target.value)} rows={4} maxLength={5000} placeholder="What you did and how, in a few sentences — enough that the approver could repeat it." />
                <p className="text-tiny text-text-muted">{writeUp.trim().length < 20 ? `${Math.max(0, 20 - writeUp.trim().length)} more characters` : 'Good.'}</p>
              </div>
            ) : docMode === 'link' ? (
              <div className="relative">
                <Link2 className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
                <Input value={docLink} onChange={(e) => setDocLink(e.target.value)} placeholder="https:// — a Notion page, Google Doc, GitHub README…" className="pl-8" inputMode="url" />
              </div>
            ) : (
              <Slot step={1} title="Document" hint={`PDF, Word, Excel, PowerPoint… up to ${MAX_UPLOAD_MB} MB`} accept={DOC_ACCEPT} icon={<FileText className="size-5" />} value={doc} onChange={setDoc} />
            )}
          </section>
          <Slot step={2} title="Photo" hint={`A screenshot or picture of the result · up to ${MAX_UPLOAD_MB} MB`} accept={PHOTO_ACCEPT} icon={<ImagePlus className="size-5" />} isImage value={photo} onChange={setPhoto} />
          <AttachmentsField
            label="More photos"
            imagesOnly
            existing={existingExtras}
            onChange={setExtraPhotos}
            onBusyChange={setExtrasBusy}
            disabled={send.isPending}
          />

          <section className={cn('rounded-lg border bg-card p-3', linkOk ? 'border-success/40 bg-success-soft/30' : tried ? 'border-danger/50' : 'border-border')}>
            <div className="mb-2 flex items-center gap-2">
              <span className={cn('flex size-5 items-center justify-center rounded-full text-micro font-semibold', linkOk ? 'bg-success text-white' : 'bg-brand-soft text-brand')}>
                {linkOk ? <CheckCircle2 className="size-3.5" /> : 3}
              </span>
              <h3 className="text-sm font-semibold">Link</h3>
              <span className="ml-auto text-micro uppercase tracking-wide text-danger">Required</span>
            </div>
            <div className="relative">
              <Link2 className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https:// — the doc, PR, deploy or folder to look at" className="pl-8" inputMode="url" />
            </div>
            {tried && !linkOk ? <p className="mt-1 text-tiny text-danger">Add a link that starts with https://</p> : null}
          </section>

          <section className={cn('rounded-lg border bg-card p-3', points.length > 0 ? 'border-success/40 bg-success-soft/30' : tried ? 'border-danger/50' : 'border-border')}>
            <div className="mb-2 flex items-center gap-2">
              <span className={cn('flex size-5 items-center justify-center rounded-full text-micro font-semibold', points.length > 0 ? 'bg-success text-white' : 'bg-brand-soft text-brand')}>
                {points.length > 0 ? <CheckCircle2 className="size-3.5" /> : 4}
              </span>
              <h3 className="flex items-center gap-1.5 text-sm font-semibold"><ListChecks className="size-4 text-text-muted" /> What was done</h3>
              <span className="ml-auto text-micro uppercase tracking-wide text-danger">Required</span>
            </div>
            <ul className="grid gap-1.5">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="size-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                  <Input
                    value={b}
                    maxLength={300}
                    placeholder={i === 0 ? 'e.g. Fixed the export so dates keep their timezone' : 'Another point'}
                    onChange={(e) => setBullets(bullets.map((x, j) => (j === i ? e.target.value : x)))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (bullets.length < 12 && b.trim()) setBullets([...bullets.slice(0, i + 1), '', ...bullets.slice(i + 1)])
                      }
                    }}
                    autoFocus={i > 0 && i === bullets.length - 1 && b === ''}
                  />
                  {bullets.length > 1 ? (
                    <Button type="button" variant="ghost" size="sm" aria-label="Remove point" onClick={() => setBullets(bullets.filter((_, j) => j !== i))}>
                      <X className="size-4" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {bullets.length < 12 ? (
              <Button type="button" variant="ghost" size="sm" className="mt-1.5 gap-1 text-brand" onClick={() => setBullets([...bullets, ''])}>
                <Plus className="size-3.5" /> Add point
              </Button>
            ) : null}
          </section>
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <p className="text-tiny text-text-muted">{ready ? 'Everything is attached.' : `Still needed: ${missing.join(', ')}.`}</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={send.isPending} onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="button"
              disabled={send.isPending || extrasBusy || (docMode === 'file' && doc?.state === 'uploading') || photo?.state === 'uploading'}
              onClick={() => {
                if (!ready) return setTried(true)
                send.mutate()
              }}
            >
              {send.isPending ? 'Sending…' : 'Send for approval'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
